import { RawCarData } from 'api/cars'
import { AnyBreakpoints, AppTheme as InternalAppTheme, BREAKPOINTS_ORDER } from 'application/theme'
import { replacePlaceholders } from 'lib/placeholders'
import { SourceProps } from 'ui/image'

import { CarViewSettings } from '../types'

/**
 * Создает готовые объекты картинок, но при этом учитывает imagesSettings.show
 * @param car
 * @param imagesSettings
 * @param imagesAdaptivitySettings
 * @param appTheme
 * @returns
 */
export const createCarImages = (
  car: RawCarData,
  imagesSettings: CarViewSettings['images'],
  imagesAdaptivitySettings: CarViewSettings['images']['adaptive'],
  appTheme: InternalAppTheme,
  title?: string
) => {
  const images: string[] = []
  imagesSettings.show?.forEach((variant) => {
    if (variant === 'stock') images.push(...car.stock_images_id)
    if (variant === 'dealer') images.push(...car.dealer_images_id)
  })

  return createImages(images, car.placeholders, imagesSettings, imagesAdaptivitySettings, appTheme, title)
}

/**
 * Создаёт готовые объекты картинок
 * @param images
 * @param placeholders
 * @param imagesSettings
 * @param imagesAdaptivitySettings
 * @param appTheme
 * @returns
 */
export const createImages = (
  images: string[],
  placeholders: RawCarData['placeholders'],
  imagesSettings: CarViewSettings['images'],
  imagesAdaptivitySettings: CarViewSettings['images']['adaptive'],
  appTheme: InternalAppTheme,
  title?: string
) => {
  const altWithPlaceholders = replacePlaceholders(imagesSettings.titleTemplate, placeholders)
  const alt = altWithPlaceholders || title || ''
  if (!images.length) {
    return [
      {
        alt: alt,
        title: replacePlaceholders(imagesSettings.titleTemplate, placeholders),
        fit: imagesSettings.fit,
        lazy: true,
        ratio: imagesSettings.ratio,
        sources: createImageSources(imagesSettings.default, imagesSettings, imagesSettings.adaptive, appTheme),
        src: `${imagesSettings.cdnUrl}/media/image/${imagesSettings.default}`
      }
    ]
  } else {
    return images.map((imageId, i) => {
      return {
        alt: `${alt}-${i + 1}`,
        title: replacePlaceholders(imagesSettings.titleTemplate, placeholders),
        fit: imagesSettings.fit,
        lazy: true,
        ratio: imagesSettings.ratio,
        sources: createImageSources(imageId, imagesSettings, imagesAdaptivitySettings, appTheme),
        src: `${imagesSettings.cdnUrl}/media/image/${imageId}`
      }
    })
  }
}
/**
 * Создаёт объекты, описывающие sources для картинки
 * @param imageId
 * @param imagesSettings
 * @param theme
 */
const createImageSources = (
  imageId: string,
  imagesSettings: CarViewSettings['images'],
  imagesAdaptivitySettings: CarViewSettings['images']['adaptive'],
  theme: InternalAppTheme
): SourceProps[] => {
  const sources: SourceProps[] = []
  if (imagesAdaptivitySettings) {
    for (let i = BREAKPOINTS_ORDER.length - 1; i >= 0; i--) {
      const breakpointName = BREAKPOINTS_ORDER[i]
      const settingsForBreakpoint = imagesAdaptivitySettings[breakpointName]
      if (settingsForBreakpoint) {
        const { width = 0, height = 0 } = settingsForBreakpoint
        if (imagesSettings.webp)
          sources.push({
            src: createImageUrl(imagesSettings.cdnUrl, imageId, true, width, height),
            mediaRule: theme.mediaQueries[breakpointName],
            contentType: 'image/webp'
          })
        sources.push({
          src: createImageUrl(imagesSettings.cdnUrl, imageId, false, width, height),
          mediaRule: theme.mediaQueries[breakpointName]
        })
      }
    }
  }
  return sources
}
const createImageUrl = (cdnUrl: string, id: string, webp: boolean, width: number, height: number): string => {
  return `${cdnUrl}/media/resized_image/${webp ? 'webp/' : ''}${id}/${width}/${height}`
}

export const createThumbsAdaptivitySettings = (carViewSettings: CarViewSettings) => {
  return carViewSettings.images.adaptive
    ? (Object.fromEntries(
        Object.entries(carViewSettings.images.adaptive).map(([key, { width, height }]) => {
          return [key, { width: width ? width / 4 : width, height: height ? height / 4 : height }]
        })
      ) as AnyBreakpoints<{ width?: number; height?: number }>)
    : undefined
}
